"use client";

import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";

import { Dialog } from "@/components/shared/Dialog";
import { Button } from "@/components/ui/button";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: React.ReactNode;
  confirmText?: string;
  confirmIcon?: React.ReactNode;
  cancelText?: string;
  variant?: "default" | "destructive";
  loading?: boolean;
  autoCloseOnConfirm?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  confirmIcon,
  cancelText,
  loading = false,
  autoCloseOnConfirm = true,
}: ConfirmDialogProps) {
  const t = useTranslations("common");

  const handleConfirm = () => {
    onConfirm();
    if (autoCloseOnConfirm && !loading) {
      onClose();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {cancelText || t("cancel")}
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={loading}>
            {loading ? <Loader2 className="size-4 animate-spin" /> : confirmIcon}
            {confirmText || t("continue")}
          </Button>
        </div>
      }
    />
  );
}
